import { __extends } from "tslib";
import { AbstractMethodError } from '@awayjs/core';
import { MethodEvent } from '../events/MethodEvent';
import { MethodBase } from './MethodBase';
/**
 * CompositeMethodBase provides a base class for diffuse methods that wrap a diffuse method to alter the
 * calculated diffuse reflection strength.
 */
var CompositeMethodBase = /** @class */ (function (_super) {
    __extends(CompositeMethodBase, _super);
    /**
     * Creates a new <code>CompositeMethodBase</code> object.
     *
     * @param modulateMethod The method which will add the code to alter the base method's strength. It needs to have the signature clampDiffuse(t:ShaderRegisterElement, regCache:ShaderRegisterCache):string, in which t.w will contain the diffuse strength.
     * @param baseMethod The base diffuse method on which this method's shading is based.
     */
    function CompositeMethodBase(baseMethod) {
        if (baseMethod === void 0) { baseMethod = null; }
        var _this = _super.call(this) || this;
        _this._onShaderInvalidatedDelegate = function (event) { return _this.onShaderInvalidated(event); };
        _this._baseMethod = baseMethod || _this.createBaseMethod();
        _this._baseMethod.addEventListener(MethodEvent.SHADER_INVALIDATED, _this._onShaderInvalidatedDelegate);
        return _this;
    }
    Object.defineProperty(CompositeMethodBase.prototype, "baseMethod", {
        /**
         * The base diffuse method on which this method's shading is based.
         */
        get: function () {
            return this._baseMethod;
        },
        set: function (value) {
            if (this._baseMethod == value)
                return;
            this._baseMethod.removeEventListener(MethodEvent.SHADER_INVALIDATED, this._onShaderInvalidatedDelegate);
            this._baseMethod = value;
            this._baseMethod.addEventListener(MethodEvent.SHADER_INVALIDATED, this._onShaderInvalidatedDelegate);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    CompositeMethodBase.prototype.createBaseMethod = function () {
        throw new AbstractMethodError();
    };
    CompositeMethodBase.prototype.iAddOwner = function (owner) {
        _super.prototype.iAddOwner.call(this, owner);
        this._baseMethod.iAddOwner(owner);
    };
    CompositeMethodBase.prototype.iRemoveOwner = function (owner) {
        _super.prototype.iRemoveOwner.call(this, owner);
        this._baseMethod.iRemoveOwner(owner);
    };
    /**
     * @inheritDoc
     */
    CompositeMethodBase.prototype.dispose = function () {
        this._baseMethod.removeEventListener(MethodEvent.SHADER_INVALIDATED, this._onShaderInvalidatedDelegate);
    };
    /**
     * Called when the base method's shader code is invalidated.
     */
    CompositeMethodBase.prototype.onShaderInvalidated = function (event) {
        this.invalidateShaderProgram();
    };
    return CompositeMethodBase;
}(MethodBase));
export { CompositeMethodBase };
import { AbstractionBase } from '@awayjs/core';
import { ChunkVO } from '@awayjs/renderer';
/**
 * _Shader_CompositeMethodBase provides a base class for diffuse methods that wrap a diffuse method to alter the
 * calculated diffuse reflection strength.
 */
var _Shader_CompositeMethodBase = /** @class */ (function (_super) {
    __extends(_Shader_CompositeMethodBase, _super);
    /**
     * Creates a new <code>_Shader_CompositeMethodBase</code> object.
     *
     * @param modulateMethod The method which will add the code to alter the base method's strength. It needs to have the signature clampDiffuse(t:ShaderRegisterElement, regCache:ShaderRegisterCache):string, in which t.w will contain the diffuse strength.
     * @param baseMethod The base diffuse method on which this method's shading is based.
     */
    function _Shader_CompositeMethodBase(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this.chunkVO = new ChunkVO();
        _this._baseChunk = method.baseMethod.getAbstraction(shader);
        return _this;
    }
    _Shader_CompositeMethodBase.prototype._isUsed = function () {
        return true;
    };
    _Shader_CompositeMethodBase.prototype._usesTangentSpace = function () {
        return this._baseChunk._usesTangentSpace();
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._initVO = function (chunkVO) {
        this._baseChunk._initVO(chunkVO);
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._initConstants = function () {
        this._baseChunk._initConstants();
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._activate = function () {
        this._baseChunk._activate();
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._setRenderState = function (renderState) {
        this._baseChunk._setRenderState(renderState);
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._deactivate = function () {
        this._baseChunk._deactivate();
        this._invalid = false;
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._getVertexCode = function (registerCache, sharedRegisters) {
        return this._baseChunk._getVertexCode(registerCache, sharedRegisters);
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        return this._baseChunk._getFragmentCode(targetReg, registerCache, sharedRegisters);
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._reset = function (chunkVO) {
        this._baseChunk._reset(chunkVO);
        this._invalid = true;
        this._cleanCompilationData();
    };
    /**
     * @inheritDoc
     */
    _Shader_CompositeMethodBase.prototype._cleanCompilationData = function () {
        this._baseChunk._cleanCompilationData();
    };
    return _Shader_CompositeMethodBase;
}(AbstractionBase));
export { _Shader_CompositeMethodBase };
/**
 * LightingCompositeBase provides a base class for diffuse methods that wrap a diffuse method to alter the
 * calculated diffuse reflection strength.
 */
var _Shader_LightingCompositeMethod = /** @class */ (function (_super) {
    __extends(_Shader_LightingCompositeMethod, _super);
    function _Shader_LightingCompositeMethod() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_LightingCompositeMethod.prototype._getFragmentPreLightingCode = function (registerCache, sharedRegisters) {
        return this._baseChunk._getFragmentPreLightingCode(registerCache, sharedRegisters);
    };
    /**
     * @inheritDoc
     */
    _Shader_LightingCompositeMethod.prototype._getFragmentCodePerLight = function (lightDirReg, lightColReg, registerCache, sharedRegisters) {
        var code = this._baseChunk._getFragmentCodePerLight(lightDirReg, lightColReg, registerCache, sharedRegisters);
        this._totalLightColorReg = this._baseChunk._totalLightColorReg;
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_LightingCompositeMethod.prototype._getFragmentCodePerProbe = function (cubeMapReg, weightRegister, registerCache, sharedRegisters) {
        var code = this._baseChunk._getFragmentCodePerProbe(cubeMapReg, weightRegister, registerCache, sharedRegisters);
        this._totalLightColorReg = this._baseChunk._totalLightColorReg;
        return code;
    };
    return _Shader_LightingCompositeMethod;
}(_Shader_CompositeMethodBase));
export { _Shader_LightingCompositeMethod };
